import clsx from 'clsx';
import type { MetricHistoryEntry } from '@arcanum-hq/shared-types';
import { Chip } from '@/components/ui/Chip';
import {
  formatByDisplayFormat,
  formatCurrency,
  formatPercent,
} from '@/lib/format';
import { ActionButtonCluster } from './ActionButtonCluster';
import { PerformanceTile } from './PerformanceTile';
import type { ThresholdFormat } from './ThresholdBand';

interface OutputDef {
  name: string;
  displayName: string;
  dataType: string;
  category: string;
  displayFormat?: string;
}

interface OutputOverride {
  panel?: string;
  displayFormatOverride?: string;
  thresholds?: {
    warn?: { below?: number; above?: number };
    error?: { below?: number; above?: number };
    feedbackText?: string;
    feedbackFix?: string;
  };
  sortOrder?: number;
}

interface DisplayConfig {
  performanceOutputs?: string[];
  capitalStackOutputs?: string[];
  outputOverrides?: Record<string, OutputOverride>;
}

interface NamedRangeDef {
  name: string;
  displayName: string;
  dataType?: string;
  displayFormat?: string;
}

interface ModelOverviewPanelProps {
  metrics: Record<string, number>;
  outputDefinitions?: OutputDef[];
  allNamedRanges?: NamedRangeDef[];
  displayConfig?: DisplayConfig;
  userInputCount: number;
  totalInputs: number;
  /**
   * Per-metric ring buffer keyed by metric name (Phase 1 `recordEdit()`
   * writeback). Drives the `<PerformanceTile>` delta. Optional — panel
   * renders without deltas when absent or empty.
   */
  metricHistory?: Record<string, MetricHistoryEntry[]>;
  /**
   * Phase 3 Session 4 (restructured 2026-05-07) — fires the agent's
   * `compare_scenarios` call with `kind: 'compact_3axis'` for an inline
   * `<ScenarioSummaryCard>` render in the chat. Caller routes through the
   * existing `handleSend` synthetic-prompt path.
   */
  onRunScenario: () => void;
  /**
   * Phase 3 Session 4 (restructured 2026-05-07) — downloads the
   * provenance audit-trail JSON for the active conversation. Caller wires
   * the actual fetch + blob.
   */
  onDownloadProvenance: () => void;
  /** Disable Run Scenario while a stream is in-flight or model isn't ready. */
  runScenarioDisabled?: boolean;
  /** Disable Provenance download (e.g. while no conversation is active). */
  provenanceDisabled?: boolean;
}

interface MetricItem {
  name: string;
  label: string;
  value: string;
  rawValue: number | null;
  status: 'ok' | 'warn' | 'error' | 'neutral';
  thresholds?: OutputOverride['thresholds'];
  format: ThresholdFormat;
}

const statusColors = {
  ok: 'text-accent',
  warn: 'text-warn',
  error: 'text-danger',
  neutral: 'text-text-bright',
};

/**
 * Names of outputs that ship from ingestion with the wrong `dataType` /
 * `displayFormat` and need to be rendered as a multiple (e.g. `1.21×`)
 * regardless of what the schema says. Schema-level fix lives in
 * `cell-classifier.ts` (would require re-ingest); this set is the narrow
 * frontend escape hatch until that lands. KEEP TIGHT — every entry is a
 * documented misclassification, not a stylistic preference.
 *
 * `EquityMultiple` (multifamily-v1 cell `MonthlyCashFlow!D76`) ships as
 * `dataType: 'currency'`, `displayFormat: '$#,##0'` because the ingestion
 * cell-classifier reads the workbook number format as currency-prefixed.
 * Surfaced 2026-05-06 — Las Vegas highrise panel rendered "$1" while the
 * chat narrative + tool result said "1.21×".
 */
const MULTIPLE_OVERRIDE_NAMES = new Set<string>([
  'EquityMultiple',
  'GPEquityMultiple',
  'LPEquityMultiple',
]);

/** Format using config-driven format — NO name heuristics except the
 *  documented `MULTIPLE_OVERRIDE_NAMES` escape hatch above. */
function formatMetricValue(
  name: string,
  value: number,
  outputDef?: OutputDef,
  override?: OutputOverride
): string {
  if (MULTIPLE_OVERRIDE_NAMES.has(name)) return `${value.toFixed(2)}×`;
  if (override?.displayFormatOverride)
    return formatByDisplayFormat(value, override.displayFormatOverride);
  if (outputDef?.displayFormat)
    return formatByDisplayFormat(value, outputDef.displayFormat);
  if (outputDef?.dataType === 'percentage') return formatPercent(value);
  if (
    outputDef?.dataType === 'currency' ||
    outputDef?.dataType === 'currency_per_sf'
  )
    return formatCurrency(value);
  if (Math.abs(value) >= 1000) return value.toLocaleString();
  return value.toFixed(2);
}

/** Status from config thresholds only */
function getStatus(
  value: number,
  thresholds?: OutputOverride['thresholds']
): 'ok' | 'warn' | 'error' | 'neutral' {
  if (!thresholds) return 'neutral';
  if (thresholds.error?.below !== undefined && value < thresholds.error.below)
    return 'error';
  if (thresholds.error?.above !== undefined && value > thresholds.error.above)
    return 'error';
  if (thresholds.warn?.below !== undefined && value < thresholds.warn.below)
    return 'warn';
  if (thresholds.warn?.above !== undefined && value > thresholds.warn.above)
    return 'warn';
  return 'ok';
}

/** Feedback from config thresholds only */
function buildFeedback(
  metrics: Record<string, number>,
  overrides?: Record<string, OutputOverride>
): { text: string; fix: string; severity: 'ok' | 'warn' | 'error' }[] {
  if (!overrides) return [];
  const feedback: {
    text: string;
    fix: string;
    severity: 'ok' | 'warn' | 'error';
  }[] = [];
  for (const [name, override] of Object.entries(overrides)) {
    if (!override.thresholds) continue;
    const value = metrics[name];
    if (value === undefined) continue;
    const status = getStatus(value, override.thresholds);
    if (status === 'warn' || status === 'error') {
      feedback.push({
        text:
          override.thresholds.feedbackText || `${name} outside target range`,
        fix: override.thresholds.feedbackFix || '',
        severity: status,
      });
    }
  }
  if (feedback.length === 0 && Object.keys(metrics).length > 0) {
    feedback.push({
      text: 'All metrics within acceptable ranges',
      fix: '',
      severity: 'ok',
    });
  }
  return feedback;
}

function deriveThresholdFormat(
  def: OutputDef | undefined,
  override: OutputOverride | undefined,
  name: string
): ThresholdFormat {
  // Frontend escape hatch for outputs ingested with a misclassified type
  // (see MULTIPLE_OVERRIDE_NAMES above). Equity Multiple ships as `currency`
  // but renders as a multiplier.
  if (MULTIPLE_OVERRIDE_NAMES.has(name)) return 'multiplier';
  const fmt = override?.displayFormatOverride ?? def?.displayFormat ?? '';
  if (fmt.includes('%') || def?.dataType === 'percentage') return 'percentage';
  if (
    fmt.includes('$') ||
    def?.dataType === 'currency' ||
    def?.dataType === 'currency_per_sf'
  )
    return 'currency';
  if (fmt.includes('x') || fmt.includes('×')) return 'multiplier';
  return 'multiplier';
}

function buildMetricItems(
  names: string[],
  metrics: Record<string, number | null | undefined>,
  outputMap: Map<string, OutputDef>,
  overrides: Record<string, OutputOverride>
): MetricItem[] {
  return names.map((name) => {
    const def = outputMap.get(name);
    const override = overrides[name];
    const value = metrics[name];
    // Use `!= null` (catches both null and undefined) instead of
    // `!== undefined`. The agent's tool returns can include explicit null
    // for outputs that can't compute yet (e.g. cash-flow-derived metrics
    // before AcquisitionDate is set). Pre-fix, those null values fell
    // through to `value.toFixed(2)` and crashed the panel render.
    // Surfaced 2026-05-04 testing operating-asset full-mode locally.
    const isFinite = value != null && Number.isFinite(value);
    return {
      name,
      label: def?.displayName || name,
      value: isFinite
        ? formatMetricValue(name, value as number, def, override)
        : '—',
      rawValue: isFinite ? (value as number) : null,
      status: isFinite
        ? getStatus(value as number, override?.thresholds)
        : 'neutral',
      thresholds: override?.thresholds,
      format: deriveThresholdFormat(def, override, name),
    };
  });
}

export function ModelOverviewPanel({
  metrics,
  outputDefinitions,
  allNamedRanges,
  displayConfig,
  userInputCount,
  totalInputs,
  metricHistory,
  onRunScenario,
  onDownloadProvenance,
  runScenarioDisabled = false,
  provenanceDisabled = false,
}: ModelOverviewPanelProps) {
  // PERFORMANCE + CAPITAL STACK gate (new-development-input-roundtrip
  // 2026-05-26): showing engine-default metrics on a freshly-created
  // conversation with zero user inputs misled users into thinking the deal
  // had real data. Pre-fix the gate was `Object.keys(metrics).length > 0`
  // which fires as soon as the engine's schema-default cellValues populate.
  // Now AND with `userInputCount > 0` so panels stay collapsed behind the
  // "Set inputs to see live metrics" placeholder until the user touches
  // state via set_inputs / set_property_address / accept_defaults. The
  // builder-side `collected` numerator counts user-set + market-set +
  // confirmed-default entries — any of those flipping to >0 reveals the
  // panels, which matches the empty-state placeholder's wording.
  const hasMetrics = Object.keys(metrics).length > 0 && userInputCount > 0;
  // Build lookup from outputs first, then overlay allNamedRanges for broader coverage
  const outputMap = new Map(outputDefinitions?.map((o) => [o.name, o]) ?? []);
  if (allNamedRanges) {
    for (const r of allNamedRanges) {
      if (!outputMap.has(r.name)) {
        outputMap.set(r.name, {
          name: r.name,
          displayName: r.displayName,
          dataType: r.dataType ?? 'unknown',
          category: 'other',
          displayFormat: r.displayFormat,
        });
      }
    }
  }
  const overrides = displayConfig?.outputOverrides ?? {};
  const perfNames = displayConfig?.performanceOutputs ?? [];
  const capitalNames = displayConfig?.capitalStackOutputs ?? [];
  const perfMetrics = buildMetricItems(
    perfNames,
    metrics,
    outputMap,
    overrides
  );
  const capitalMetrics = buildMetricItems(
    capitalNames,
    metrics,
    outputMap,
    overrides
  );
  const feedback = buildFeedback(metrics, overrides);

  return (
    <div className="flex flex-col gap-0.5">
      <div className="px-4 py-3 border-b border-panel-border flex items-center justify-between">
        <span className="text-xs font-mono font-semibold text-text-muted tracking-[1.2px]">
          MODEL OVERVIEW
        </span>
        <span
          className="text-xs font-mono text-text-dim"
          data-testid="model-overview-counter"
        >
          {userInputCount}/{totalInputs}
        </span>
      </div>
      {!hasMetrics ? (
        <div className="px-4 py-6 text-center">
          <div className="text-sm text-text-dim font-mono">
            No model data yet
          </div>
          <div className="text-xs text-text-dim font-mono mt-1">
            Set inputs to see live metrics
          </div>
        </div>
      ) : (
        <>
          {perfMetrics.length > 0 && (
            <div className="px-4 py-3">
              <div className="text-micro font-mono text-text-dim tracking-[0.8px] mb-2">
                PERFORMANCE
              </div>
              <div className="grid grid-cols-2 gap-3">
                {perfMetrics.map((m) => (
                  <PerformanceTile
                    key={m.name}
                    name={m.name}
                    label={m.label}
                    value={m.rawValue}
                    format={m.format}
                    thresholds={m.thresholds}
                    history={metricHistory?.[m.name]}
                  />
                ))}
              </div>
            </div>
          )}
          {capitalMetrics.length > 0 && (
            <div className="px-4 py-3 border-t border-panel-border">
              <div className="text-micro font-mono text-text-dim tracking-[0.8px] mb-2">
                CAPITAL STACK
              </div>
              <div className="space-y-1.5">
                {capitalMetrics.map((m) => (
                  <div
                    key={m.name}
                    className="flex justify-between items-baseline gap-2"
                  >
                    <span className="flex items-center gap-1.5 min-w-0">
                      <Chip variant="agent" label="Agent" />
                      <span className="text-xs font-mono text-text-muted truncate">
                        {m.label}
                      </span>
                    </span>
                    <span className="text-sm font-mono text-text-bright tabular-nums font-medium shrink-0">
                      {m.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {feedback.length > 0 && (
            <div className="px-4 py-3 border-t border-panel-border">
              <div className="text-micro font-mono text-text-dim tracking-[0.8px] mb-2">
                FEEDBACK
              </div>
              <div className="space-y-2">
                {feedback.map((f, i) => (
                  <div key={i}>
                    <div
                      className={clsx(
                        'text-xs font-mono font-medium',
                        statusColors[f.severity]
                      )}
                    >
                      {f.severity !== 'ok' ? '⚠ ' : '✓ '}
                      {f.text}
                    </div>
                    {f.fix && (
                      <div className="text-micro font-mono text-text-dim mt-0.5 ml-4">
                        → {f.fix}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="px-4 py-3 border-t border-panel-border">
            <ActionButtonCluster
              onRunScenario={onRunScenario}
              onDownloadProvenance={onDownloadProvenance}
              runScenarioDisabled={runScenarioDisabled}
              provenanceDisabled={provenanceDisabled}
            />
          </div>
        </>
      )}
    </div>
  );
}
